/**
 * Overlay source adapters (M9-B) — one per country, all behind the SAME
 * {@link OverlayAdapter} contract so the engine, render, and UI never branch on
 * source. Adding a country = adding an adapter here (ZERO engine changes).
 *
 * `overlayCatalog()` is browser-safe static metadata (labels/units/citation +
 * tiers) — the variable picker lists it with no data loaded and no network.
 */

import type { Country, DemographicVariable, OverlaySource, Tier } from "../types";
import type { OverlayAdapter } from "./types";
import { createUsCensusAdapter } from "./usCensus";
import { createStatCanAdapter } from "./statCan";
import { createUkPostcodeAdapter } from "./ukPostcode";

export type { HttpClient, HttpRequestInit, OverlayAdapter } from "./types";
export { createFetchHttpClient, createFixtureHttpClient, withParams, type HttpFixtures } from "./http";
export * from "./usCensus";
export * from "./statCan";
export * from "./ukPostcode";

/** Construct all built-in adapters (fresh instances; adapters are stateless). */
export function createAllAdapters(): OverlayAdapter[] {
  return [createUsCensusAdapter(), createStatCanAdapter(), createUkPostcodeAdapter()];
}

/** One picker group: a source+country and its variables + tiers (static). */
export interface OverlayCatalogGroup {
  source: OverlaySource;
  country: Country;
  tiers: Tier[];
  variables: DemographicVariable[];
}

/**
 * The full variable catalog for the picker, grouped by source/country. Pure +
 * browser-safe: no network, no DB — just the adapters' static metadata. An
 * optional `sources` filter narrows to the deployment's configured sources.
 */
export function overlayCatalog(sources?: OverlaySource[]): OverlayCatalogGroup[] {
  const wanted = sources ? new Set(sources) : null;
  return createAllAdapters()
    .filter((a) => !wanted || wanted.has(a.source))
    .map((a) => ({
      source: a.source,
      country: a.country,
      tiers: [...a.tiers],
      variables: a.catalog(),
    }));
}
